import { useCallback, useMemo, useState } from 'react'
import { useSessionStorage } from 'react-use'
import { arrayMove } from '@dnd-kit/sortable'
import { v4 as uuid4 } from 'uuid'

import { ErrorResponse } from '@makyoapp/makyo-sdk/api/api'
import { IBody, IBodySettings } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table.js'
import { BuilderDroppableElement } from '@makyoapp/makyo-common'

import { ViewComponent } from '@/core/app-builder/app/component'
import { IAppLayer } from '@/core/app-builder/app/layer'
import { parsePageTitle } from '@/core/app-builder/parser/variable-text'
import { getComponentLabel } from '@/core/app-builder/undo'
import constate from '@/lib/constate'
import { toSdkOverlay } from '@/adapters/__project__/BuilderOverlayAdapter'
import { toSdkPage, toSdkPageBody } from '@/adapters/__project__/BuilderPageAdapter'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppClientContext } from '@/context/__project__/AppClientContext'
import { useBuilderAccordionContext } from '@/context/__project__/BuilderAccordionContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppOverlaysContext } from '@/context/AppOverlaysContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { useReqError } from '@/hooks/useReqError'
import { ComponentSettingKey, FlattenedItem, TreeItem, TreeItems } from '@/shared/builder'
import { flatten } from '@/shared/builder-navigator'
import { replaceTableIdsRecursive } from '@/shared/collect-table-ids'
import {
  appendToBody,
  checkIsSubmitButton,
  cloneInBody,
  cloneWithNewIds,
  findComponentInBody,
  updateNestedBody,
} from '@/utils/builder/app-builder'
import { ColumnTypeToFieldMap, FORM_FIELD_COLUMN_TYPES } from '@/utils/builder/form-parsers'

import { buildTree, filterParentDeepAlt, removeChildrenOf, splitGlobalAndPageElements } from '../utils'

interface ClipboardItem {
  pageID: string
  pageName: string
  elID: string
  elName: string
  elComponentID: string
}

/**
 * SortableTreeContext
 *
 * @todo:
 * - [ ] use builderGlobals for rendering
 */
const [SortableTreeProvider, useSortableTreeContext] = constate('SortableTreeContext', () => {
  const printAPIError = useReqError()

  const { appClient } = useAppClientContext()
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { builderGlobals, setShowFabWarningModal, setFabWarningReason } = useAppBuilderContext()
  const { pages } = useAppPagesContext()
  const { overlays } = useAppOverlaysContext()
  const {
    activeLayer,
    activeLayerContainer,
    activeLayerClient,
    activeLayerDetailRowId,
    activeLayerElements,
    activeComponent,
    setActiveComponent,
    activeLayerElementSettings,
  } = useActiveLayerContext()

  // Load the collapsed state object from localStorage
  const [collapsedState, setCollapsedState] = useSessionStorage<Record<string, boolean>>('collapsedState', {})

  const applyCollapsedState = useCallback(
    (items: BuilderDroppableElement[], collapsedState: Record<string, boolean>): TreeItem[] => {
      return items.map((item) => {
        const collapsed =
          collapsedState[item.id] !== undefined ? collapsedState[item.id] : ((item as TreeItem).collapsed ?? false)

        // Recursively apply to children
        const children = applyCollapsedState(item.children, collapsedState)

        return {
          ...(item as TreeItem),
          collapsed,
          children,
        }
      })
    },
    []
  )

  const elementsWithGlobals = useMemo(() => {
    // Base elements from current layer
    const base = activeLayerElements || []

    // Only apply for pages
    if (activeLayer.type !== 'page') return base

    const globalsBody = builderGlobals.elements || []
    if (!globalsBody.length) return base

    const pageHasHeader = base.some((e) => e.compId === 'header')
    const pageHasFooter = base.some((e) => e.compId === 'footer')

    const showHeader = !pageHasHeader
    const showFooter = !pageHasFooter

    const headerBodies = globalsBody.filter((b) => b.compId === 'header' && b.groupId === 'globals')
    const footerBodies = globalsBody.filter((b) => b.compId === 'footer' && b.groupId === 'globals')

    const headerEls = showHeader
      ? headerBodies.map((el) => ({
          ...el,
          draggable: false,
          removable: true,
        }))
      : []
    const footerEls = showFooter
      ? footerBodies.map((el) => ({
          ...el,
          draggable: false,
          removable: true,
        }))
      : []

    // Deduplicate: if an element (by id) exists in page base, remove it from globals view
    const collectIdsDeep = (items: BuilderDroppableElement[], acc: Set<string> = new Set()): Set<string> => {
      for (const it of items || []) {
        acc.add(it.id)
        if (it.children?.length) collectIdsDeep(it.children, acc)
      }
      return acc
    }
    const pruneByIds = (items: BuilderDroppableElement[], exclude: Set<string>): BuilderDroppableElement[] => {
      const result: BuilderDroppableElement[] = []
      for (const it of items || []) {
        if (exclude.has(it.id)) continue
        const prunedChildren = pruneByIds(it.children || [], exclude)
        result.push({ ...it, children: prunedChildren })
      }
      return result
    }
    const pageIds = collectIdsDeep(base)
    const headerPruned = pruneByIds(headerEls, pageIds)
    const footerPruned = pruneByIds(footerEls, pageIds)

    return [...headerPruned, ...base, ...footerPruned]
  }, [activeLayer.type, activeLayerElements, builderGlobals])

  const elements = useMemo(
    () => applyCollapsedState(elementsWithGlobals || [], collapsedState),
    [applyCollapsedState, elementsWithGlobals, collapsedState]
  )

  const { removeAccordions } = useBuilderAccordionContext()

  //--------------------------------------------------------------------------------------//
  //                                  Clipboard Actions                               //
  //--------------------------------------------------------------------------------------//
  // Clipboard
  const [clipboard, setClipboard] = useState<{ pageId: string; elId: string } | null>(null)
  const [clipboardAll, setClipboardAll] = useState<{ pageId: string; elements: TreeItems } | null>(null)
  const [formClipboard, setFormClipboard] = useState<ClipboardItem | null>(null)

  const clearClipboard = useCallback(() => {
    setClipboard(null)
    setClipboardAll(null)
    setFormClipboard(null)
  }, [setClipboard, setClipboardAll])

  //--------------------------------------------------------------------------------------//
  //                                  DnD Context                                         //
  //--------------------------------------------------------------------------------------//

  const [activeId, setActiveId] = useState<string | null>(null)

  const findDataValueSettings = useCallback(
    (id: string) => {
      const elSettings = activeLayerElementSettings[id]
      if (!elSettings) return
      const dataValueSettingsKey = Object.keys(elSettings).find((key) => key.includes('-data-value'))
      return dataValueSettingsKey ? (elSettings[dataValueSettingsKey] as string) : undefined
    },
    [activeLayerElementSettings]
  )

  // flattened items to be displayed
  const flattenedItems: FlattenedItem[] = useMemo(() => {
    let items = elements ? flatten(elements) : []

    return items.map((item) => {
      // apply collapsed state
      if (collapsedState[item.id]) {
        item.collapsed = true
      }

      if (item.isForm) {
        // Check for form container error
        const hasFormContainer = items.find((i) => i.id === item.parentId || i.compId === 'form')

        if (!hasFormContainer) {
          return {
            ...item,
            formErrorType: 'no-container',
          }
        }

        const columnId = findDataValueSettings(item.id)

        if (!columnId) {
          return {
            ...item,
            formErrorType: 'no-column',
          }
        }

        // Check for duplicate columnId found in other elements
        const hasDuplicateColumn = !!items
          .filter((i) => {
            if (i.id === item.id || i.groupId !== 'form') return false
            if (i.parentType === 'form' && item.parentType === 'form') {
              return i.parentId === item.parentId
            }
            if (i.parentType === 'form' && item.parentType !== 'form') {
              const formParent = filterParentDeepAlt(items, item.parentId, (i) => i.compId === 'form')
              if (!formParent) return false
              return i.parentId === formParent.id
            } else if (i.parentType !== 'form' && item.parentType === 'form') {
              const formParent = filterParentDeepAlt(items, i.parentId, (i) => i.compId === 'form')
              if (!formParent) return false
              return item.parentId === formParent.id
            } else {
              const parent1 = filterParentDeepAlt(items, i.parentId, (i) => i.compId === 'form')
              const parent2 = filterParentDeepAlt(items, item.parentId, (i) => i.compId === 'form')
              if (!parent1 || !parent2) return false
              return parent1.id === parent2.id
            }
          })
          .find((i) => findDataValueSettings(i.id) === columnId)

        if (hasDuplicateColumn) {
          return {
            ...item,
            formErrorType: 'duplicate-column',
          }
        }
      }

      return item
    })
  }, [collapsedState, elements, findDataValueSettings])

  // flattened items map for faster lookups
  const flattenedItemsMap = useMemo(() => {
    const map = new Map<string, FlattenedItem>()
    flattenedItems.forEach((item) => {
      map.set(item.id, item)
    })
    return map
  }, [flattenedItems])

  // flattened tree items to be displayed
  const sortableTreeItems = useMemo(() => {
    if (!flattenedItems.length) return []

    const collapsedItems = flattenedItems?.reduce<string[]>(
      (acc, { children, collapsed, id }) => (collapsed && children.length ? [...acc, id] : acc),
      []
    )

    return removeChildrenOf(flattenedItems, activeId != null ? [activeId, ...collapsedItems] : collapsedItems)
  }, [activeId, flattenedItems])

  /**
   * Handle reorder of the elements
   * @param items - The items to reorder
   */
  const handleReorder = useCallback(
    async (newFlattenedItems: FlattenedItem[]) => {
      if (!activeLayerClient || !appClient || !appClient.builderSettings) return

      /**
       * Helper to compare if two flattened item arrays have the same structure
       * Only compares essential properties: id, parentId, depth, and order
       */
      const hasStructureChanged = (prev: FlattenedItem[], next: FlattenedItem[]): boolean => {
        if (prev.length !== next.length) return true

        for (let i = 0; i < prev.length; i++) {
          const prevItem = prev[i]
          const nextItem = next[i]

          // Compare only the essential properties that indicate a reorder
          if (
            prevItem.id !== nextItem.id ||
            prevItem.parentId !== nextItem.parentId ||
            prevItem.depth !== nextItem.depth
          ) {
            return true
          }
        }

        return false
      }

      // ?debug
      // console.log('newFlattenedItems', newFlattenedItems)

      const prevPageElements = activeLayerElements
      const prevPageFlatElements = flatten(prevPageElements)
      const prevPageSettings = activeLayerClient.settings._BodySettings

      const prevGlobalsElements = builderGlobals.elements
      const prevGlobalsFlatElements = flatten(prevGlobalsElements)
      const prevGlobalsSettings = builderGlobals.settings

      const combinedSettings = { ...prevPageSettings, ...prevGlobalsSettings }

      // Split root items: page roots vs global roots (Header/Footer)
      const [newGlobalFlatElements, newPageFlatElements] = splitGlobalAndPageElements(
        newFlattenedItems,
        flattenedItemsMap
      )

      // Update page body (exclude globals)
      const pageChanged = hasStructureChanged(prevPageFlatElements, newPageFlatElements)

      if (pageChanged) {
        const newPageTree = buildTree(newPageFlatElements)
        const newPageBody = toSdkPageBody(newPageTree)

        // update new body settings from new pageFlatElements
        const newPageSettings = {
          ...Object.fromEntries(newPageFlatElements.map((it) => [it.id, combinedSettings[it.id]])),
        }

        // ?debug
        // console.log('Page changed:', {
        //   newPageFlatElements,
        //   newPageTree,
        // })

        await activeLayerClient.Update({ _Body: newPageBody, _BodySettings: newPageSettings }, true, {
          description: `Reordered page elements`,
        })
      }

      // Only update globals if there are global elements AND they've changed
      if (newGlobalFlatElements.length === 0) return

      const globalsChanged = hasStructureChanged(prevGlobalsFlatElements, newGlobalFlatElements)

      if (globalsChanged) {
        const newGlobalsTree = buildTree(newGlobalFlatElements)
        const newGlobalsBody = toSdkPageBody(newGlobalsTree)

        const newGlobalsSettings = {
          ...Object.fromEntries(newGlobalFlatElements.map((it) => [it.id, combinedSettings[it.id]])),
        }

        // ?debug
        // console.log('Globals changed:', {
        //   newGlobalFlatElements,
        //   newGlobalsTree,
        // })

        await activeLayerClient.appClient.UpdateGlobals({
          _Body: newGlobalsBody,
          _BodySettings: newGlobalsSettings,
        })
      }
    },
    [
      activeLayerClient,
      activeLayerElements,
      appClient,
      builderGlobals.elements,
      builderGlobals.settings,
      flattenedItemsMap,
    ]
  )

  /**
   * Recursively find an element by id in the nested structure
   * @param items - The array of elements to search in
   * @param targetId - The id to search for
   * @returns The found element or undefined
   */
  const findElementRecursively = useCallback(
    (items: BuilderDroppableElement[], targetId: string): BuilderDroppableElement | undefined => {
      for (const item of items) {
        if (item.id === targetId) {
          return item
        }

        // Search in children if they exist
        if (item.children && item.children.length > 0) {
          const found = findElementRecursively(item.children, targetId)
          if (found) {
            return found
          }
        }
      }

      return undefined
    },
    []
  )

  /**
   * Handle collapse of the element
   * @param id - The id of the element to collapse
   */
  const handleCollapse = useCallback(
    (id: string) => {
      const currentItem = flattenedItems.find((item) => item.id === id)
      if (!currentItem) return

      let newState = { ...collapsedState }
      newState[id] = !currentItem.collapsed
      setCollapsedState(newState)
    },
    [collapsedState, flattenedItems, setCollapsedState]
  )

  /**
   * Handle collapse all of the elements
   */
  const handleCollapseAll = useCallback(() => {
    let newState = { ...collapsedState }

    flattenedItems.forEach((item) => {
      if (item.children.length) {
        newState[item.id] = true
      }
    })

    setCollapsedState(newState)
  }, [collapsedState, flattenedItems, setCollapsedState])

  /**
   * Handle expand all of the elements
   */
  const handleExpandAll = useCallback(() => {
    let newState = { ...collapsedState }

    flattenedItems.forEach((item) => {
      if (item.children.length) {
        newState[item.id] = false
      }
    })

    setCollapsedState(newState)
  }, [collapsedState, flattenedItems, setCollapsedState])

  /**
   * Handle copy all of the elements
   */
  const handleCopyAll = useCallback(() => {
    if (activeLayerContainer) {
      const allElements = activeLayerContainer.elements ?? []

      // Store the entire tree in the clipboard
      setClipboardAll({
        pageId: activeLayerContainer?.id,
        elements: allElements, // Store all elements in clipboard
      })

      setClipboard(null)
    }
  }, [activeLayerContainer, setClipboardAll, setClipboard])

  /**
   * Handle paste all of the elements
   */
  const handlePasteAll = useCallback(async () => {
    if (clipboardAll && clipboardAll.elements && activeLayerClient) {
      const sourcePage = pages?.find((p) => p.id === clipboardAll.pageId)
      const sourceOverlay = overlays?.find((o) => o.id === clipboardAll.pageId)

      let originalSettings
      if (sourcePage) {
        originalSettings = sourcePage.elementSettings
      } else if (sourceOverlay) {
        originalSettings = sourceOverlay.elementSettings
      } else {
        return
      }

      const childIdMap = new Map<string, string>()

      const existingFab = activeLayerClient.settings._Body.some((component) => component._ComponentID === 'fab')

      if (existingFab) {
        setFabWarningReason?.('limit')
        setShowFabWarningModal(true)
        return
      }
      // Helper: Clone components with new IDs recursively
      const copyComponentWithNewIds = (component: IBody): IBody => {
        const newId = uuid4() // Generate unique ID
        childIdMap.set(component._id, newId)

        const copiedChildren = component._Body?.map(copyComponentWithNewIds) || []

        return {
          ...component,
          _id: newId,
          _Body: copiedChildren,
        }
      }

      // Helper: Add component to components map
      const addComponentToMap = (component: IBody, pageSettings: IBodySettings) => {
        const newViewComponent = new ViewComponent(activeLayerClient, component)
        activeLayerClient.componentsMap.set(component._id, newViewComponent)

        if (component._Body && component._Body.length > 0) {
          component._Body.forEach((child) => addComponentToMap(child, pageSettings))
        }
      }

      // Clone all components from the clipboard
      const sdkTarget = toSdkPageBody(clipboardAll.elements)
      const copiedComponents = sdkTarget.map(copyComponentWithNewIds)

      // Append copied components to the body
      const newBodyArray = [...activeLayerClient.settings._Body, ...copiedComponents]

      // Update body settings
      let layerSettings = { ...activeLayerClient.settings._BodySettings }

      // Map original settings to the new components using the childIdMap
      childIdMap.forEach((newId, oldId) => {
        if (originalSettings[oldId]) {
          layerSettings[newId] = originalSettings[oldId]
        }
      })

      const sourcePageTableID = sourcePage?.tableId
      const newPageTableID = activeLayerClient.settings._TableID

      // update detail page reference recursively
      if (sourcePageTableID && newPageTableID && sourcePageTableID !== newPageTableID) {
        layerSettings = Object.fromEntries(
          Object.entries(layerSettings).map(([key, value]) => [
            key,
            replaceTableIdsRecursive(sourcePageTableID, newPageTableID, value),
          ])
        )
      }

      // Update body settings
      copiedComponents.forEach((component) => {
        addComponentToMap(component, layerSettings)
      })

      const updatedPage = {
        ...activeLayerClient.settings,
        _Body: newBodyArray,
        _BodySettings: layerSettings,
      }

      // Trigger an update
      try {
        await activeLayerClient.Update(updatedPage, true, {
          description: `Pasted ${clipboardAll.elements.length} elements from ${sourcePage?.title || sourceOverlay?.title}`,
        })
      } catch (error) {
        printAPIError(error as ErrorResponse)
      }
    }
  }, [clipboardAll, activeLayerClient, pages, overlays, setShowFabWarningModal, setFabWarningReason, printAPIError])

  //--------------------------------------------------------------------------------------//
  //                                  Component Actions                                   //
  //--------------------------------------------------------------------------------------//

  /**
   * Handle copy of the element
   * @param id - The id of the element to copy
   */
  const handleCopy = useCallback(
    (id: string) => {
      if (!activeLayer) return

      const item = findElementRecursively(elements || [], id)

      if (!item) return

      setClipboard({
        pageId: activeLayer.id,
        elId: id,
      })
      setClipboardAll(null)
    },
    [activeLayer, elements, findElementRecursively, setClipboard, setClipboardAll]
  )

  /**
   * Handle paste of the element
   */
  const handlePaste = useCallback(async () => {
    // Check if clipboard data exists
    if (!clipboard || !activeLayerClient) return

    const sourcePage = pages?.find((p) => p.id === clipboard.pageId)
    const sourceOverlay = overlays?.find((o) => o.id === clipboard.pageId)

    // Resolve possible sources: current page/overlay OR project-level globals
    let componentToCopy: IBody | undefined
    let sourceBodySettings: IBodySettings = {}
    let sourcePageTableID: string | undefined

    let sdkSource: any
    if (sourcePage) {
      sdkSource = toSdkPage(sourcePage)
    } else if (sourceOverlay) {
      sdkSource = toSdkOverlay(sourceOverlay)
    }

    // Try to find in page/overlay body first
    if (sdkSource) {
      const pageMatch = findComponentInBody(sdkSource._Body || [], clipboard.elId)
      if (pageMatch) {
        componentToCopy = pageMatch
        sourceBodySettings = sdkSource?._BodySettings ?? {}
        sourcePageTableID = sdkSource._TableID
      }
    }

    // If not found, try in project-level globals
    if (!componentToCopy) {
      const globals = ((activeLayerClient.appClient?.builderSettings as any)?._Globals || {
        _Body: [],
        _BodySettings: {},
      }) as { _Body: IBody[]; _BodySettings: IBodySettings }
      const globalMatch = findComponentInBody(globals._Body || [], clipboard.elId)
      if (globalMatch) {
        componentToCopy = globalMatch
        sourceBodySettings = globals._BodySettings || {}
      }
    }

    if (!componentToCopy) return

    const isFAB = componentToCopy._ComponentID === 'fab'
    if (isFAB && activeLayer.type === 'overlay') return

    if (isFAB) {
      const alreadyHasFAB = activeLayerClient.settings._Body.some((component) => component._ComponentID === 'fab')
      if (alreadyHasFAB) {
        setShowFabWarningModal(true)
        return
      }
    }
    const newBodySettings = activeLayerClient.settings._BodySettings ?? {}

    // Clone the component with new IDs to prevent conflicts
    const copiedComponent = cloneWithNewIds(componentToCopy, sourceBodySettings, newBodySettings)

    const newPageTableID = activeLayerClient.settings._TableID

    // update detail page reference recursively
    if (sourcePageTableID && newPageTableID && sourcePageTableID !== newPageTableID) {
      newBodySettings[copiedComponent._id] = replaceTableIdsRecursive(
        sourcePageTableID,
        newPageTableID,
        newBodySettings[copiedComponent._id]
      )
    }

    // Insert the cloned component into the target's body
    const bodyArray = [...(activeLayerClient.settings._Body ?? [])]
    const newBodyArray = appendToBody(bodyArray, copiedComponent)

    // Helper function to add the component to the map recursively
    const addComponentToMap = (component: IBody, pageSettings: IBodySettings) => {
      const newViewComponent = new ViewComponent(activeLayerClient, component)
      activeLayerClient.componentsMap.set(component._id, newViewComponent)

      // Recursively handle nested components
      if (component._Body && component._Body.length > 0) {
        component._Body.forEach((childComponent) => addComponentToMap(childComponent, pageSettings))
      }
    }

    // Add the cloned component to the map
    addComponentToMap(copiedComponent, newBodySettings)

    const updatedLayer = {
      ...activeLayerClient.settings,
      _Body: newBodyArray,
      _BodySettings: newBodySettings,
    }

    try {
      // Update the active layer (page or overlay) with the new component
      await activeLayerClient.Update(updatedLayer, true, {
        description: `Copied ${getComponentLabel(copiedComponent)}`,
      })
    } catch (error) {
      printAPIError(error as ErrorResponse)
    } finally {
      setClipboard(null)
    }
  }, [activeLayer.type, activeLayerClient, clipboard, overlays, pages, printAPIError, setShowFabWarningModal])

  /**
   * Handle clone of the element
   * @param componentId - The id of the element to clone
   */
  const handleClone = useCallback(
    (componentId: string) => {
      if (!activeLayerClient) return

      const item = flattenedItems.find((item) => item.id === componentId)
      if (!item) return

      // If this element (or any ancestor) is part of project-level globals, duplicate in _Globals and return
      const globals = ((appClient?.builderSettings as any)?._Globals || { _Body: [], _BodySettings: {} }) as any
      const containsInBodies = (bodies: any[], targetId: string): boolean => {
        for (const b of bodies) {
          if (b._id === targetId) return true
          if (b._Body?.length && containsInBodies(b._Body, targetId)) return true
        }
        return false
      }
      if (containsInBodies(globals._Body || [], componentId)) {
        const sourceComponent = findComponentInBody(globals._Body || [], componentId)
        if (!sourceComponent) return

        // Prevent cloning FAB within Global Footer (limit 1)
        if (sourceComponent._ComponentID === 'fab') {
          setFabWarningReason?.('limit')
          setShowFabWarningModal(true)
          return
        }

        // Collect settings for the subtree to clone
        const collectComponentSettings = (component: any, settings: Record<string, any>) => {
          const componentSettings = settings[component._id] ?? {}
          const allSettings: Record<string, any> = {
            [component._id]: componentSettings,
          }
          if (component._Body && component._Body.length > 0) {
            component._Body.forEach((child: any) => {
              const childSettings = collectComponentSettings(child, settings)
              Object.assign(allSettings, childSettings)
            })
          }
          return allSettings
        }

        const originalSettings = collectComponentSettings(sourceComponent, globals._BodySettings || {})
        const newGlobalsSettings = { ...(globals._BodySettings || {}) }
        const clonedComponent = cloneWithNewIds(sourceComponent, originalSettings, newGlobalsSettings)
        const newGlobalsBody = cloneInBody(globals._Body || [], componentId, clonedComponent)

        activeLayerClient.appClient.UpdateGlobals({
          _Body: newGlobalsBody as any,
          _BodySettings: newGlobalsSettings,
        })
        return
      }

      // Step 1: Find the component to clone in the _Body array
      const componentToClone = findComponentInBody(activeLayerClient.settings._Body || [], componentId)

      if (!componentToClone || !activeLayerClient.settings._BodySettings) {
        return
      }

      const isFAB = componentToClone._ComponentID === 'fab'
      if (isFAB && activeLayer.type === 'overlay') return

      if (isFAB) {
        const alreadyHasFAB = activeLayerClient.settings._Body.some((component) => component._ComponentID === 'fab')
        if (alreadyHasFAB) {
          setFabWarningReason?.('limit')
          setShowFabWarningModal(true)
          return
        }
      }

      const bodyArray = activeLayerClient.settings._Body ?? []
      const bodySettings = activeLayerClient.settings._BodySettings ?? {}

      // Get the settings of the component to clone and all its nested components
      const collectComponentSettings = (component: IBody, settings: IBodySettings) => {
        const componentSettings = settings[component._id] ?? {}
        const allSettings: IBodySettings = {
          [component._id]: componentSettings,
        }

        // Recursively collect settings for nested components
        if (component._Body && component._Body.length > 0) {
          component._Body.forEach((child) => {
            const childSettings = collectComponentSettings(child, settings)
            Object.assign(allSettings, childSettings)
          })
        }

        return allSettings
      }

      const originalSettings = collectComponentSettings(componentToClone, bodySettings)

      // Clone the component and generate new IDs for it and its nested components
      const clonedComponent = cloneWithNewIds(componentToClone, originalSettings, bodySettings)

      // Update the BodySettings with all nested component settings
      const newBodySettings = { ...activeLayerClient.settings._BodySettings, ...bodySettings }

      // Insert the cloned component into the _Body array after the original component
      const newBody = cloneInBody(bodyArray, componentId, clonedComponent)

      // Add all components to the map recursively
      const addComponentToMap = (component: IBody) => {
        const newComponent = new ViewComponent(activeLayerClient, component)
        activeLayerClient.componentsMap.set(component._id, newComponent)

        // Recursively handle nested components
        component._Body?.forEach((childComponent) => {
          addComponentToMap(childComponent)
        })
      }

      addComponentToMap(clonedComponent)

      // Update the component in the client
      const newViewComponent = new ViewComponent(activeLayerClient, clonedComponent)

      activeLayerClient.UpdateComponent(
        newViewComponent,
        {
          bodyArray: newBody,
          bodySettings: newBodySettings,
        },
        {
          description: `Cloned ${getComponentLabel(clonedComponent)}`,
        }
      )
    },
    [
      activeLayerClient,
      flattenedItems,
      activeLayer.type,
      setShowFabWarningModal,
      setFabWarningReason,
      appClient?.builderSettings,
    ]
  )

  /**
   * Handle rename of the element
   * @param componentId - The id of the element to rename
   * @param newName - The new name of the element
   */
  const handleRename = useCallback(
    (componentId: string, newName: string) => {
      if (!activeLayerClient) return

      const item = flattenedItems.find((item) => item.id === componentId)
      if (!item) return

      const component = activeLayerClient?.componentsMap.get(item.id)
      if (!component) return

      component.Rename(newName)
    },
    [activeLayerClient, flattenedItems]
  )

  /**
   * Handle removal of the element
   * @param componentId - The id of the element to remove
   */
  const handleRemove = useCallback(
    async (componentId: string) => {
      if (!activeLayerClient) return

      const item = flattenedItems.find((item) => item.id === componentId)
      if (!item) return

      // If this element (or any ancestor) is part of project-level globals, delete from _Globals and return
      const globals = ((appClient?.builderSettings as any)?._Globals || { _Body: [], _BodySettings: {} }) as any
      const containsInBodies = (bodies: any[], targetId: string): boolean => {
        for (const b of bodies) {
          if (b._id === targetId) return true
          if (b._Body?.length && containsInBodies(b._Body, targetId)) return true
        }
        return false
      }
      if (containsInBodies(globals._Body || [], componentId)) {
        const collectIds = (b: any): string[] => {
          const ids: string[] = [b._id]
          for (const c of b._Body || []) ids.push(...collectIds(c))
          return ids
        }
        const findById = (bodies: any[], targetId: string): any | undefined => {
          for (const b of bodies) {
            if (b._id === targetId) return b
            const f = findById(b._Body || [], targetId)
            if (f) return f
          }
          return undefined
        }
        const removeFromBodies = (bodies: any[], targetId: string): any[] => {
          const result: any[] = []
          for (const b of bodies) {
            if (b._id === targetId) continue
            const children = b._Body?.length ? removeFromBodies(b._Body, targetId) : []
            result.push({ ...b, _Body: children })
          }
          return result
        }

        const node = findById(globals._Body || [], componentId)
        let newSettings = { ...(globals._BodySettings || {}) }
        if (node) {
          for (const id of collectIds(node)) delete newSettings[id]
        }
        const newBody = removeFromBodies(globals._Body || [], componentId)

        await activeLayerClient.appClient.UpdateGlobals({ _Body: newBody, _BodySettings: newSettings })
        return
      }

      if (item.compId !== 'tab-item') {
        setActiveComponent(undefined)
        removeAccordions(componentId)
      }

      activeLayerClient.DeleteComponent(componentId)
    },
    [activeLayerClient, flattenedItems, removeAccordions, setActiveComponent, appClient?.builderSettings]
  )

  const isInGlobals = useCallback(
    (componentId: string): boolean => {
      const item = flattenedItemsMap.get(componentId)
      if (!item) return false

      // Check if the item itself is a root-level global component
      if (item.groupId === 'globals' && !item.parentId) return true

      // Walk up the parent chain to find a global root component
      let currentId: string | null | undefined = item.parentId
      while (currentId) {
        const parent = flattenedItemsMap.get(currentId)
        if (!parent) break

        // If we find a root-level global component (Header/Footer)
        if (parent.groupId === 'globals' && !parent.parentId) {
          return true
        }

        currentId = parent.parentId
      }

      return false
    },
    [flattenedItemsMap]
  )

  /**
   * Move an existing page component into project-level globals under a target global parent
   */
  const moveToGlobals = useCallback(
    async (componentId: string, targetParentId: string): Promise<boolean> => {
      if (!activeLayerClient || !appClient) return false

      const sourceComponent = findComponentInBody(activeLayerClient.settings._Body || [], componentId)
      if (!sourceComponent) return false

      const collectIds = (b: IBody): string[] => {
        const ids: string[] = [b._id]
        for (const c of b._Body || []) ids.push(...collectIds(c))
        return ids
      }
      const allIds = collectIds(sourceComponent)

      // Remove from page body recursively
      const removeFromBodies = (bodies: IBody[], targetId: string): IBody[] => {
        const result: IBody[] = []
        for (const b of bodies) {
          if (b._id === targetId) continue
          const children = b._Body?.length ? removeFromBodies(b._Body, targetId) : []
          result.push({ ...b, _Body: children })
        }
        return result
      }

      // Extract moved settings from page and clean them from page settings
      const pageSettings = { ...(activeLayerClient.settings._BodySettings || {}) }
      const movedSettings: Record<string, any> = {}
      for (const id of allIds) {
        if (pageSettings[id] !== undefined) {
          movedSettings[id] = pageSettings[id]
          delete pageSettings[id]
        }
      }

      const newPageBody = removeFromBodies(activeLayerClient.settings._Body || [], componentId)

      // Persist page update (remove the component from page)
      await activeLayerClient.Update({ _Body: newPageBody, _BodySettings: pageSettings }, true, {
        description: `Moved ${getComponentLabel(sourceComponent)} to globals`,
      })

      // Insert into globals under target parent
      const globals = ((appClient.builderSettings as any)?._Globals || { _Body: [], _BodySettings: {} }) as any

      const insertInto = (bodies: IBody[], targetId: string, child: IBody): IBody[] => {
        return (bodies || []).map((b) => {
          if (b._id === targetId) return { ...b, _Body: [...(b._Body || []), child] }
          if (Array.isArray(b._Body) && b._Body.length) return { ...b, _Body: insertInto(b._Body, targetId, child) }
          return b
        })
      }

      const newGlobalsBody = insertInto(globals._Body || [], targetParentId, sourceComponent)
      const newGlobalsSettings = { ...(globals._BodySettings || {}), ...movedSettings }

      await appClient.UpdateGlobals({ _Body: newGlobalsBody as any, _BodySettings: newGlobalsSettings })
      return true
    },
    [activeLayerClient, appClient]
  )

  /**
   * Move an existing global component into the current page (or under a specific page parent)
   * Ensures removed component is omitted from _Globals payload completely.
   */
  const moveFromGlobals = useCallback(
    async (componentId: string, targetParentId?: string): Promise<boolean> => {
      if (!activeLayerClient || !appClient || !appClient.builderSettings?._Globals) return false
      const globals = appClient.builderSettings._Globals

      const sourceComponent = findComponentInBody(globals._Body || [], componentId)
      if (!sourceComponent) return false

      const collectIds = (b: IBody): string[] => {
        const ids: string[] = [b._id]
        for (const c of b._Body || []) ids.push(...collectIds(c))
        return ids
      }
      const allIds = collectIds(sourceComponent)

      // Collect settings to transfer, and strip them from globals
      const movedSettings: Record<string, any> = {}
      const newGlobalsSettings: Record<string, any> = { ...(globals._BodySettings || {}) }
      for (const id of allIds) {
        if (newGlobalsSettings[id] !== undefined) {
          movedSettings[id] = newGlobalsSettings[id]
          delete newGlobalsSettings[id]
        }
      }

      // Remove component subtree from globals body
      const removeFromBodies = (bodies: IBody[], targetId: string): IBody[] => {
        const result: IBody[] = []
        for (const b of bodies) {
          if (b._id === targetId) continue
          const children = b._Body?.length ? removeFromBodies(b._Body, targetId) : []
          result.push({ ...b, _Body: children })
        }
        return result
      }
      const newGlobalsBody = removeFromBodies(globals._Body || [], componentId)

      await Promise.all([
        // Insert into page (root or under target parent), carrying over settings
        activeLayerClient.AddComponent(sourceComponent, movedSettings, targetParentId, undefined, {
          description: `Moved ${getComponentLabel(sourceComponent)} from globals`,
        }),
        // Persist globals update first so payload omits the removed component
        appClient.UpdateGlobals({
          _Body: newGlobalsBody as any,
          _BodySettings: newGlobalsSettings,
        }),
      ])

      return true
    },
    [activeLayerClient, appClient]
  )

  /**
   * Move an existing global component within globals
   * @param activeIndex - The index of the active component
   * @param overIndex - The index of the over component
   */
  const moveWithinGlobals = useCallback(
    async (activeIndex: number, overIndex: number) => {
      if (!appClient || !appClient.builderSettings?._Globals) return false

      const sortedItems = arrayMove(flattenedItems, activeIndex, overIndex)
      const newItems = buildTree(sortedItems)

      appClient.UpdateGlobals({
        _Body: toSdkPageBody(newItems),
        _BodySettings: appClient.builderSettings._Globals._BodySettings || {},
      })
    },
    [appClient, flattenedItems]
  )

  /**
   * Handle remove form fields of the element
   * @param id - The id of the element to remove form fields
   */
  const handleRemoveFormFields = useCallback(
    (id: string) => {
      if (!activeLayerClient?.settings || !activeLayerClient.componentsMap) return

      const component = activeLayerClient.componentsMap.get(id)
      if (!component) return

      const layerSettings = { ...activeLayerClient.settings._BodySettings }

      // Clean up old components and their settings
      for (const child of component._Body) {
        const childId = child._id
        const isSubmitButton = checkIsSubmitButton(child, layerSettings)

        // remove every settings except (submit) button
        if (!isSubmitButton) {
          delete layerSettings[childId]
          activeLayerClient.componentsMap.delete(childId)
        }
      }

      // remove all children except (submit) button
      component._Body = component._Body.filter((child) => {
        return checkIsSubmitButton(child, layerSettings)
      })

      const newBodyArray = updateNestedBody(activeLayerClient.settings._Body, component.toBody())

      const newBodySettings = { ...layerSettings }

      // update the component
      activeLayerClient.UpdateComponent(
        component,
        {
          bodyArray: newBodyArray,
          bodySettings: newBodySettings,
        },
        {
          description: `Removed form fields of ${getComponentLabel(component.toBody())}`,
        }
      )
    },
    [activeLayerClient]
  )

  /**
   * Handle copy form fields of the element
   * @param id - The id of the element to copy form fields
   */
  const handleCopyFormFields = useCallback(
    (id: string) => {
      if (!activeLayerClient) return

      // check comp
      const formToCopy = flattenedItemsMap.get(id)
      if (!formToCopy) return

      setFormClipboard({
        pageID: activeLayer.id,
        pageName: parsePageTitle(activeLayerClient, language, userSpecific, activeLayerDetailRowId),
        elID: id,
        elName: formToCopy.customTitle || formToCopy.title,
        elComponentID: formToCopy.compId,
      })
      // toast.success(`"${formToCopy.title}" fields copied to clipboard`)
    },
    [activeLayerClient, flattenedItemsMap, activeLayer.id, language, userSpecific, activeLayerDetailRowId]
  )

  /**
   * Handle paste form fields of the element
   * @param id - The id of the element to paste form fields
   */
  const handlePasteFormFields = useCallback(
    async (targetComponentID: string) => {
      if (!appClient || !dbClient || !formClipboard) return

      // ===========================================================================
      // STEP 1: mapping source and target components
      // ===========================================================================

      // get source layer (page/overlay)
      const sourcePageId = formClipboard.pageID
      const sourceLayer = appClient.pagesMap.get(sourcePageId) || appClient.overlaysMap.get(sourcePageId)
      if (!sourceLayer) return

      // get source component
      const sourceComponent = sourceLayer.componentsMap.get(formClipboard.elID)
      if (!sourceComponent) return

      const sourceLayerSettings = sourceLayer.settings._BodySettings || {}
      const sourceFormBody = sourceComponent._Body
      const sourceFormTableId = sourceLayerSettings[sourceComponent._id][ComponentSettingKey.FormDataSource]
      const sourceTable = dbClient.tablesMap.get(sourceFormTableId)
      if (!sourceTable) return

      // get target layer (page/overlay)
      const targetPageId = activeLayer.id
      const targetLayer = appClient.pagesMap.get(targetPageId) || appClient.overlaysMap.get(targetPageId)
      if (!targetLayer) return

      // get target component
      const targetLayerClient = targetLayer as IAppLayer
      const targetComponent = targetLayerClient.componentsMap.get(targetComponentID)
      if (!targetComponent) return

      let targetLayerSettings = targetLayerClient.settings._BodySettings || {}
      const targetFormTableId = targetLayerSettings[targetComponent._id][ComponentSettingKey.FormDataSource]
      const targetTable = dbClient.tablesMap.get(targetFormTableId)
      if (!targetTable) return

      // ===========================================================================
      // STEP 2: remove existing children of target component
      // ===========================================================================
      let newLayerSettings = { ...targetLayerSettings }

      const deleteSettingsRecursively = (components: IBody[]) => {
        for (const comp of components) {
          delete newLayerSettings[comp._id]
          if (comp._Body.length > 0) {
            deleteSettingsRecursively(comp._Body)
          }
        }
      }
      deleteSettingsRecursively(targetComponent._Body)

      // ===========================================================================
      // STEP 3: mapping form fields
      // ===========================================================================

      // prepare final body and settings
      let newBody: IBody[] = []
      let newBodySettings: Record<string, any> = {}

      // recreate body & settings with new id
      const recreateBodyRecursively = (children: IBody[]) => {
        return children.map((comp) => {
          let newComp = { ...comp, _id: uuid4() }
          newBodySettings[newComp._id] = { ...sourceLayerSettings[comp._id] }
          if (comp._Body.length > 0) {
            newComp._Body = recreateBodyRecursively(comp._Body)
          }
          return newComp
        })
      }
      newBody = recreateBodyRecursively([...sourceFormBody])

      // list possible columns to map in target table
      const possibleTargetTableColumns = targetTable.settings._Columns.filter((col) =>
        FORM_FIELD_COLUMN_TYPES.includes(col._Type)
      )
      let usedColumnIDs: Set<string> = new Set()

      const findPossibleColumnID = (possibleColumnTypes: Set<ColumnTypes>) => {
        if (possibleColumnTypes.size === 0) return ''

        for (const columnType of possibleColumnTypes) {
          const columns = possibleTargetTableColumns.filter((col) => col._Type === columnType)
          if (columns.length === 0) continue

          for (const column of columns) {
            if (usedColumnIDs.has(column._id)) continue
            usedColumnIDs.add(column._id)
            return column._id
          }
        }
        return ''
      }

      // map trough form body (nested) recursively to handle form field columnID
      const mapFormFields = (children: IBody[]) => {
        for (const comp of children) {
          let compSettings = newBodySettings[comp._id]

          if (comp._Body.length > 0) {
            mapFormFields(comp._Body)
          }

          if (comp._GroupID === 'form') {
            const componentID = comp._ComponentID
            const possibleColumnTypes = ColumnTypeToFieldMap.get(componentID) ?? new Set()

            const dataValueKey = Object.keys(compSettings).find((key) => key.endsWith('-data-value'))
            if (!dataValueKey) {
              continue
            }

            const value = compSettings[dataValueKey]
            const currentColumn = sourceTable.columnsMap.get(value)
            const foundInTargetColumn = targetTable.columnsMap.get(value)

            const isSameColumn =
              currentColumn &&
              foundInTargetColumn &&
              currentColumn._id === foundInTargetColumn._id &&
              currentColumn._Type === foundInTargetColumn._Type

            // case 1: same column (usually happen between source-query tables)
            if (isSameColumn) {
              newBodySettings[comp._id][dataValueKey] = value
              continue
            }

            // default: need to map column into field manually, if possible
            newBodySettings[comp._id][dataValueKey] = findPossibleColumnID(possibleColumnTypes)
            continue
          }
        }
      }
      mapFormFields(newBody)

      // build new settings
      newLayerSettings = { ...newLayerSettings, ...newBodySettings }

      // buld new form body
      targetComponent._Body = newBody
      const newLayerBody = updateNestedBody(targetLayerClient.settings._Body, targetComponent.toBody())

      // build new layer settings
      const updatedLayer = {
        ...targetLayerClient.settings,
        _Body: [...newLayerBody],
        _BodySettings: { ...newLayerSettings },
      }

      await targetLayerClient.Update(updatedLayer, true)
      // toast.success(`"${formClipboard.elName}" fields pasted successfully`)
    },
    [appClient, dbClient, formClipboard, activeLayer.id]
  )

  return {
    // DnD Context
    activeId,
    setActiveId,
    flattenedItems,
    flattenedItemsMap,
    sortableTreeItems,
    handleReorder,

    // Builder Context
    activeComponent,
    setActiveComponent,
    handleCollapse,
    handleRemove,
    handleRename,
    handleClone,
    handleCopy,
    elements,
    handleCollapseAll,
    handleExpandAll,
    handleCopyAll,
    handlePasteAll,
    setClipboard,
    setClipboardAll,
    handlePaste,
    clipboard,
    clipboardAll,
    clearClipboard,

    // form specific actions
    formClipboard,
    handleRemoveFormFields,
    handleCopyFormFields,
    handlePasteFormFields,

    activeLayerElementSettings,
    activeLayerClient,
    moveToGlobals,
    moveFromGlobals,
    moveWithinGlobals,
    isInGlobals,
  }
})

export { SortableTreeProvider, useSortableTreeContext }
